import PropTypes from "prop-types";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  checkMinAmount,
  getDiscountPrice,
  getProductCartQuantity,
} from "../../helpers/product";
import { addToCart } from "../../redux/actions/cartActions";
import {
  addToWishlist,
  deleteFromWishlist,
} from "../../redux/actions/wishlistActions";
import { useTranslation } from "react-i18next";
import { formatPrice, formatWeight } from "../../helpers/formatters";
import _ from "lodash";
import { CURRENCY } from "../../constants/DeliveryConstants";
import { Button, Dropdown, Menu, Modal } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DownOutlined,
  HeartFilled,
  HeartOutlined,
  RightOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import CartIncreaser from "../cart/CartIncreaser";
import styled from "styled-components";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import urls from "../../urls";
import { colors } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import AddToCartBtn from "../cta/AddToCartBtn";
import { useInView } from "react-intersection-observer";
import { setProductAnalyticsEvent } from "../../helpers/analytics";
import {
  ANALYTICS_ADD_TO_CART,
  ANALYTICS_VIEW_ITEM,
} from "../../constants/AnalyticsConstants";
import { PRODUCT_DETAIL_VARIANT_IMAGE } from "../../constants/ImageConstants";
import LazyImage from "../image/LazyImage";
import TrustpilotMiniWidget from "../trustpilot/TrustpilotMiniWidget";
import useIsMobile from "../hooks/useIsMobile";
import {
  findCategoryById,
  findMainCategoryBySubId,
} from "../../helpers/categories";
import ReactPixel from "react-facebook-pixel";
import { trackProductAdded } from "../../helpers/posthogAnalytics";

const ProductDescriptionInfo = ({
  product,
  discountedPrice,
  cartItems,
  addToCart,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  const grams = searchParams.get("gram");
  const minAmount = product.min_amount || 1;
  const [quantityCount, setQuantityCount] = useState(minAmount);
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [variation, setVariation] = useState(null);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [type, setType] = useState(null);
  const [addToCartRef, addToCartInView] = useInView({
    threshold: 1,
    delay: 0,
  });
  const [currentProduct, setCurrentProduct] = useState(null);
  const isMobile = useIsMobile();
  const productCartQty = getProductCartQuantity(cartItems, product);
  const products = useSelector((state) => state.firestoreReducer.data.products);
  const categories = useSelector(
    (state) => state.firestoreReducer.ordered.categories,
  );
  const wishlistItems = useSelector((state) => state.wishlistData);
  const wishlistItem = useMemo(() => {
    return wishlistItems.find((item) => item.id === product.id);
  }, [wishlistItems, product]);

  useEffect(() => {
    if (!!product) {
      let productPrice = product.price;
      let variations = product.variations;
      if (quantityCount !== product.min_amount)
        setQuantityCount(product.min_amount);
      if (!!variations && variations.length > 0) {
        // Get the first variation that matches parent price and set that as initial selected variation
        let firstMatch = _.find(variations, function (x) {
          return x.price === productPrice;
        });
        if (!!firstMatch) {
          setVariation(firstMatch);
          setSelectedWeight(firstMatch.weight);
        } else {
          setVariation(variations[0]);
          setSelectedWeight(variations[0].weight);
        }
      } else {
        setVariation(null);
        setSelectedWeight(null);
      }
      if (!!product.type && !!product.type[0]) setType(product.type[0]);
      else setType(null);

      if (product.name !== currentProduct) {
        setCurrentProduct(product.name);
        let category = findCategoryById(categories, product.categories[0]);
        if (!!category && !!category.subcategoryId) {
          category = findMainCategoryBySubId(
            categories,
            category.subcategoryId,
          );
        }
        setProductAnalyticsEvent(
          ANALYTICS_VIEW_ITEM,
          product,
          1,
          category?.name,
        );
      }

      // Get grams from url and set corresponding variation
      if (!!grams) {
        let variation = _.find(variations, function (x) {
          return x.weight === grams;
        });
        if (!!variation) {
          setVariation(variation);
          setSelectedWeight(variation.weight);
        }
      }
    }
  }, [product]);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === "production" && !!product) {
      const weight = product.variations?.length > 0 && selectedWeight;
      const productId = !!weight ? `${product.url}-${weight}` : product.url;
      ReactPixel.track("ViewContent", {
        content_ids: [productId],
        content_name: product.name,
        currency: "EUR",
        value: product.price,
      });
    }
  }, [product, selectedWeight]);

  const menu = (
    <Menu>
      {!!product.type &&
        product.type.map((productType) => {
          return (
            <Menu.Item
              key={productType}
              onClick={() => {
                setType(productType);
              }}
            >
              {productType}
            </Menu.Item>
          );
        })}
    </Menu>
  );

  return (
    <div className="product-details-content ml-70">
      {!!product.productVariants && (
        <VariantsDesktopWrapper>
          <ChooseVariantTitle>{t("choose_variant")}</ChooseVariantTitle>
          <VariantsRow>
            <Col xs={4} sm={3}>
              <VariantImage
                className={"chosen"}
                src={product.image}
                alt={product.name}
              />
              {!!product.discount && (
                <VariantDiscountTag>-{product.discount}%</VariantDiscountTag>
              )}
              <VariantDesktopName>
                <span>{product.variantName}</span>
              </VariantDesktopName>
            </Col>
            {product.productVariants.map((variant, index) => {
              return (
                <Col xs={4} sm={3} key={index}>
                  <Link
                    to={{
                      pathname: urls.product + products[variant.productId].url,
                      state: { preventScroll: true },
                    }}
                  >
                    <VariantImage
                      src={products[variant.productId].image}
                      alt={products[variant.productId].name}
                    />
                    {!!products[variant.productId].discount && (
                      <VariantDiscountTag>
                        -{products[variant.productId].discount}%
                      </VariantDiscountTag>
                    )}
                    <VariantDesktopName>
                      <span>{products[variant.productId].variantName}</span>
                    </VariantDesktopName>
                  </Link>
                </Col>
              );
            })}
          </VariantsRow>
        </VariantsDesktopWrapper>
      )}
      {!!product.productVariants && (
        <VariantsMobileWrapper className={"mt-3 mb-4"}>
          <StyledButton
            block
            onClick={() => setVariantModalVisible(!variantModalVisible)}
            shape="round"
          >
            {t("current_variant")}:{" "}
            <span className={"variant-name"}>{product.variantName}</span>
            <RightOutlined />
          </StyledButton>
          <Modal
            centered
            title={t("choose_variant")}
            footer={null}
            visible={variantModalVisible}
            onCancel={() => setVariantModalVisible(false)}
          >
            <Row>
              {product.productVariants.map((variant, index) => {
                return (
                  <Col xs={4} sm={3} className={"text-center"} key={index}>
                    <ModalVariantLink
                      to={{
                        pathname:
                          urls.product + products[variant.productId].url,
                        state: { preventScroll: true },
                      }}
                      onClick={() => setVariantModalVisible(false)}
                    >
                      <VariantImage
                        src={products[variant.productId].image}
                        alt={products[variant.productId].name}
                      />
                      {!!products[variant.productId].discount && (
                        <VariantDiscountTag>
                          -{products[variant.productId].discount}%
                        </VariantDiscountTag>
                      )}
                      {products[variant.productId].variantName}
                    </ModalVariantLink>
                  </Col>
                );
              })}
            </Row>
          </Modal>
        </VariantsMobileWrapper>
      )}
      <div className="product-details-price">
        {discountedPrice !== null ? (
          !!variation ? (
            <Fragment>
              <span>
                {CURRENCY +
                  formatPrice(
                    getDiscountPrice(variation.price, product.discount),
                  )}
              </span>{" "}
              <span className="old">
                {CURRENCY + formatPrice(variation.price)}
              </span>
            </Fragment>
          ) : (
            <Fragment>
              <span>{CURRENCY + formatPrice(discountedPrice)}</span>{" "}
              <span className="old">
                {CURRENCY + formatPrice(product.price)}
              </span>
            </Fragment>
          )
        ) : !!variation ? (
          <span>{CURRENCY + formatPrice(variation.price)}</span>
        ) : (
          <span>{CURRENCY + formatPrice(product.price)}</span>
        )}
      </div>
      <StockInfo stock={product.stock}>
        {product.stock && product.stock > 0 ? (
          <div className={"d-flex align-items-center"}>
            <CheckCircleOutlined />
            <span className={"ml-1"}>
              {product.stock < 11 ? t("limited_stock_cta") : t("in_stock")}
            </span>
          </div>
        ) : (
          <div className={"d-flex align-items-center"}>
            <CloseCircleOutlined />
            <span className={"ml-1"}>{t("out_of_stock")}</span>
          </div>
        )}
      </StockInfo>
      {!!product.type && (
        <div className="pro-details-type mb-4 mt-3">
          <span className="title">{t("choose_product_type")}</span>
          <Dropdown overlay={menu}>
            <Button>
              {type} <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      )}

      {product.variations && (
        <div className="pro-details-size-color mb-4 mt-3">
          <div className="pro-details-size">
            <span>{t("choose_weight")}</span>
            <div className="pro-details-size-content">
              {product.variations.map((entry, key) => {
                return (
                  <label
                    className={`pro-details-size-content--single`}
                    key={key}
                  >
                    <input
                      type="radio"
                      value={entry.weight}
                      checked={entry.weight === selectedWeight ? "checked" : ""}
                      onChange={() => {
                        setSelectedWeight(entry.weight);
                        setQuantityCount(1);
                        setVariation(entry);
                        // Add grams to url
                        let url =
                          urls.product + product.url + "?gram=" + entry.weight;
                        window.history.replaceState({}, "", url);
                      }}
                    />
                    <span className="size-name">
                      {formatWeight(entry.weight)} {t("gram")}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
        <div>
          <CartIncreaser
            cartItem={product}
            amount={quantityCount}
            onPressDecrease={() =>
              setQuantityCount(checkMinAmount(minAmount, quantityCount))
            }
            onPressIncrease={() =>
              setQuantityCount(
                quantityCount <= product.stock - productCartQty
                  ? quantityCount + 1
                  : quantityCount,
              )
            }
          />
          {product?.name?.toLowerCase().includes("gourmetschotel") && (
            <p style={{ marginBottom: 0, marginTop: 2, color: "#333" }}>
              {t("amount_of_people")}
            </p>
          )}
        </div>
        {!!wishlistItem ? (
          <WishlistButton
            onClick={() => dispatch(deleteFromWishlist(product))}
            title={t("remove_from_wishlist")}
          >
            <HeartFilled />
          </WishlistButton>
        ) : (
          <WishlistButton
            onClick={() => dispatch(addToWishlist(product))}
            title={t("add_to_wishlist")}
          >
            <HeartOutlined />
          </WishlistButton>
        )}
      </div>
      <div ref={addToCartRef}>
        <AddToCartBtn
          className={"mb-4 mt-3"}
          disabled={
            productCartQty > product.stock ||
            productCartQty + quantityCount > product.stock
          }
          icon={
            productCartQty > 0 ? <CheckOutlined /> : <ShoppingCartOutlined />
          }
          onClick={() => {
            addToCart(product, quantityCount, variation, type);
            setQuantityCount(minAmount);
            setProductAnalyticsEvent(
              ANALYTICS_ADD_TO_CART,
              product,
              quantityCount,
            );
            trackProductAdded(product, quantityCount);
          }}
          inCart={productCartQty > 0}
        >
          {product.stock &&
          product.stock !== 0 &&
          productCartQty < product.stock
            ? t("add_to_cart")
            : t("out_of_stock")}
        </AddToCartBtn>
      </div>
      {/*<div className={"d-flex mb-3"}>*/}
      {/*  <div className="pro-details-wishlist">*/}
      {/*    <button*/}
      {/*      className={wishlistItem !== undefined ? "active" : ""}*/}
      {/*      disabled={wishlistItem !== undefined}*/}
      {/*      title={*/}
      {/*        wishlistItem !== undefined*/}
      {/*          ? t("added_to_wishlist")*/}
      {/*          : t("add_to_wishlist")*/}
      {/*      }*/}
      {/*      onClick={() => addToWishlist(product)}*/}
      {/*    >*/}
      {/*      <i className="pe-7s-like" />*/}
      {/*    </button>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <SellingPointsWrapper>
        {t("cart_usps", { returnObjects: true }).map((item, index) => {
          return (
            <SellingPoint key={index}>
              <img
                src={"/assets/img/icons/knives-icon-primaryDark.webp"}
                alt={""}
              />{" "}
              <p>{item}</p>
            </SellingPoint>
          );
        })}
      </SellingPointsWrapper>

      {!isMobile && (
        <TrustpilotMiniWidget style={{ marginTop: "1.25rem" }} scale={0.5} />
      )}

      <MobileStickyBar show={addToCartInView ? 0 : 1}>
        <AddToCartBtn
          disabled={
            productCartQty >= product.stock ||
            productCartQty + quantityCount >= product.stock
          }
          icon={
            productCartQty > 0 ? <CheckOutlined /> : <ShoppingCartOutlined />
          }
          onClick={() => {
            addToCart(product, quantityCount, variation, type, true);
            setQuantityCount(minAmount);
            setProductAnalyticsEvent(
              ANALYTICS_ADD_TO_CART,
              product,
              quantityCount,
            );
            trackProductAdded(product, quantityCount);
          }}
          inCart={productCartQty > 0}
        >
          {product.stock &&
          product.stock !== 0 &&
          productCartQty < product.stock
            ? t("add_to_cart")
            : t("out_of_stock")}
        </AddToCartBtn>
      </MobileStickyBar>
    </div>
  );
};

const VariantDesktopName = styled.div`
  text-align: center;

  span {
    color: dimgrey;
    font-size: 13px;
    font-weight: 500;
  }
`;

const SellingPoint = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;

  img {
    width: 21px;
    height: 100%;
  }

  p {
    font-size: 15px;
    margin-bottom: 0;
  }
`;

const SellingPointsWrapper = styled.ul`
  margin-bottom: 1rem;
`;

const StyledButton = styled(Button)`
  .variant-name {
    font-weight: 700;
    margin-left: 4px;
  }

  .anticon {
    margin-left: 0;
  }
`;

const MobileStickyBar = styled.div`
  background: white;
  box-shadow: 0 0 10px #0000001f;
  position: fixed;
  bottom: ${({ show }) => (show ? 0 : "-60px")};
  top: auto;
  left: 0;
  z-index: 99;
  width: 100%;
  padding: 8px 12px 8px;

  transition: 0.4s bottom ease-in-out;

  @media ${device.lg} {
    display: none;
  }
`;

const StockInfo = styled.span`
  // Red for no stock, orange for limited stock, green for in stock
  color: ${({ stock }) =>
    stock < 11 ? (stock === 0 ? "#ff0000" : "#fd6500 ") : "#00a900"};
`;

const VariantDiscountTag = styled.div`
  background: ${colors.primary};
  color: #fff;
  font-size: 13px;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 3px;

  position: absolute;
  top: -8px;
  right: 10px;
`;

const VariantImage = styled(LazyImage)`
  width: 100%;
  height: auto;
  transition: 0.2s all ease;
  border-radius: 3px;
  border: 2px solid #dfdfdf;

  &:hover {
    border-color: #bbbbbb;
  }

  &.chosen {
    border-color: ${colors.primary};
  }

  min-height: ${PRODUCT_DETAIL_VARIANT_IMAGE.sm.height + "px"};
  @media ${device.md} {
    min-height: ${PRODUCT_DETAIL_VARIANT_IMAGE.md.height + "px"};
  }
  @media ${device.lg} {
    min-height: ${PRODUCT_DETAIL_VARIANT_IMAGE.lg.height + "px"};
  }
  @media ${device.xl} {
    min-height: ${PRODUCT_DETAIL_VARIANT_IMAGE.xl.height + "px"};
  }
`;

const ModalVariantLink = styled(Link)`
  color: #333;
`;

const VariantsMobileWrapper = styled.div`
  @media ${device.lg} {
    display: none !important;
  }
`;

const ChooseVariantTitle = styled.h4`
  font-size: 17px;
  color: #333;
`;

const VariantsRow = styled(Row)`
  width: 100%;
  height: auto;
`;

const VariantsDesktopWrapper = styled(Row)`
  display: none !important;
  @media ${device.lg} {
    display: flex !important;
    margin-left: 0;
    margin-right: 0;
    border-bottom: 1px solid lightgrey;
    padding-bottom: 1rem;
  }
`;

const WishlistButton = styled.button`
  height: 40px;
  width: 40px;
  border: none;
  background: none;
  padding: 0;

  .anticon {
    font-size: 36px;
    color: ${colors.primary};

    &:hover {
      color: ${colors.primaryHover};
    }
  }
`;

ProductDescriptionInfo.propTypes = {
  addToCart: PropTypes.func,
  cartItems: PropTypes.array,
  discountedPrice: PropTypes.number,
  product: PropTypes.object,
};

const mapDispatchToProps = (dispatch) => {
  return {
    addToCart: (item, quantityCount, variation, type, omitNotification) => {
      dispatch(
        addToCart(item, quantityCount, variation, type, omitNotification),
      );
    },
  };
};

export default connect(null, mapDispatchToProps)(ProductDescriptionInfo);
